import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { ft, szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { zarasMentese } from '../reszek/zaras-dokumentum.ts';

/**
 * Havi zárás. (a megrendelő kérése, 2026-09-28)
 *
 * Egy hónap egyetlen lapon — ugyanúgy, ahogy a napi zárás összesítője
 * mutatja a napot. Minden szám mellett ott az **előző hónap** és a
 * változás, mert a puszta összeg önmagában keveset mond.
 *
 * A mennyiség ugyanaz, amit a **Napi bontás** mutat: a lezárt napok és a
 * régi Excelből behozott forgalmi előzmény együtt. Az érték és az áfa
 * csak a lezárt napokból — az előzményben nincs ár.
 */

interface ZarasSzam {
  cimke: string;
  ertek: number;
  egyseg: string;
  elozo: number;
  valtozas: number | null;
  megjegyzes?: string;
}

interface ZarasCsoport {
  cim: string;
  szamok: ZarasSzam[];
}

interface Zaras {
  honap: string;
  elozo_honap: string;
  lezart_napok: number;
  elozo_lezart_napok: number;
  van_adat: boolean;
  csoportok: ZarasCsoport[];
}

/** A számot a mértékegysége szerint formázzuk. */
function ertekSzoveg(sz: ZarasSzam): string {
  if (sz.egyseg === 'Ft/liter') return szam(sz.ertek, 1);
  return ft(sz.ertek);
}

export function HaviZaras() {
  const letolthet = useMuvelet('export_kimutatas');
  const [honapok, setHonapok] = useState<string[]>([]);
  const [honap, setHonap] = useState('');
  const [adat, setAdat] = useState<Zaras | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ honapok: string[] }>('/api/kimutatas/honapok')
      .then((v) => {
        setHonapok(v.honapok);
        // A legfrissebb hónappal indulunk, akármilyen sorrendben jön a lista.
        const legfrissebb = [...v.honapok].sort().pop();
        setHonap((h) => h || legfrissebb || new Date().toISOString().slice(0, 7));
      })
      .catch((h: Error) => setHiba(h.message));
  }, []);

  const betolt = useCallback(async () => {
    if (!honap) return;
    try {
      setAdat(await api.get<Zaras>(`/api/kimutatas/zaras/${honap}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A havi zárást nem sikerült betölteni.');
    }
  }, [honap]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Havi zárás</span>
        <select value={honap} onChange={(e) => setHonap(e.target.value)}>
          {honapok.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
          {honapok.length === 0 && <option value="">nincs lezárt hónap</option>}
        </select>
        <span className="hint">
          A <b>lezárt napokból</b> és a forgalmi előzményből. Minden szám mellett az előző hónap és a
          változás.
        </span>
        <div className="stats">
          <div>
            Lezárt nap<strong>{adat?.lezart_napok ?? 0}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}

      <div className="actbar" style={{ marginTop: 12 }}>
        {/* A napi zárás rövid kimutatásának mintájára: egy A4-es lap. */}
        <button
          className="btn pri sm"
          disabled={!adat?.van_adat}
          title="Egyetlen A4-es lap, nyomtatásból PDF-be menthető"
          onClick={() => void zarasMentese(honap).catch((h: Error) => setHiba(h.message))}
        >
          Kimutatás (nyomtatás / PDF)
        </button>
        {letolthet && (
          <button
            className="btn sm"
            disabled={!adat?.van_adat}
            onClick={() => letolt(`/api/export/zaras/${honap}`)}
          >
            Excel
          </button>
        )}
        <span className="hint">
          {adat ? `Összevetve: ${adat.elozo_honap} (${adat.elozo_lezart_napok} lezárt nap)` : ''}
        </span>
      </div>

      {adat && !adat.van_adat && (
        <div className="blank" style={{ marginTop: 16 }}>
          Ebben a hónapban még nincs lezárt nap, ezért nincs mit összesíteni.
        </div>
      )}

      {adat?.van_adat &&
        adat.csoportok.map((cs) => (
          <div key={cs.cim} style={{ marginTop: 16 }}>
            <h3 className="szakaszcim">{cs.cim}</h3>
            <div className="cards">
              {cs.szamok.map((sz) => (
                <div className="card" key={sz.cimke}>
                  <em>{sz.cimke}</em>
                  <b>{ertekSzoveg(sz)}</b>
                  <span>
                    {sz.egyseg}
                    {sz.megjegyzes ? ` · ${sz.megjegyzes}` : ''}
                  </span>
                  <div className={`zvalt${(sz.valtozas ?? 0) < 0 ? ' le' : ''}`}>
                    {sz.valtozas === null ? (
                      <span className="halvany">előző hónap: —</span>
                    ) : (
                      <>
                        {sz.valtozas > 0 ? '▲' : sz.valtozas < 0 ? '▼' : '■'}{' '}
                        {szam(Math.abs(sz.valtozas), 1)}%
                        <span className="halvany"> · előző: {ertekSzoveg({ ...sz, ertek: sz.elozo })}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {adat?.van_adat && adat.lezart_napok === 0 && (
        <div className="uzenet" style={{ marginTop: 16 }}>
          Ebben a hónapban még nem a programban dolgoztatok: csak a régi Excelből behozott
          mennyiség látszik, ár és érték nélkül.
        </div>
      )}

      {adat?.van_adat && (
        <div className="uzenet flag" style={{ marginTop: 16 }}>
          Az <b>eladott mennyiség</b> ugyanannyi, mint a Napi bontásban: a lezárt napok és a régi
          Excelből behozott forgalmi előzmény együtt. Az <b>érték, az áfa és az átlagár</b> csak a
          lezárt napokból jön, mert az előzményben nincs ár. Ami még ellenőrzés alatt van, az itt
          nem látszik — zárjátok le, és a szám magától a helyére kerül.
        </div>
      )}
    </>
  );
}
